import {
  getOrgStudies,
  getOrgCourses,
  getOrgMembers,
  getStudyMetrics,
  getCourseMetrics,
  getStudyQuestions
} from './firebase';
import {
  OrganizationAnalytics,
  StudyAnalyticsItem,
  CourseAnalyticsItem,
  QuestionAnalytics,
  LearnerStats,
  Study,
  Course,
  UserProfile,
  Question
} from '../types';

/**
 * Get comprehensive analytics for an organization
 */
export const getOrganizationAnalytics = async (orgId: string): Promise<OrganizationAnalytics> => {
  // Fetch all base data in parallel
  const [studies, courses, members] = await Promise.all([
    getOrgStudies(orgId),
    getOrgCourses(orgId),
    getOrgMembers(orgId)
  ]);

  // Fetch metrics for studies and courses in parallel
  const [studyMetricsResults, courseMetricsResults] = await Promise.all([
    Promise.all(studies.map(study => getStudyMetrics(study.id))),
    Promise.all(courses.map(course => getCourseMetrics(course.id)))
  ]);

  // Fetch questions for all studies
  const questionResults = await Promise.all(
    studies.map(study => getStudyQuestions(study.id))
  );
  const allQuestions = questionResults.flat();

  // Build study analytics items
  const studyAnalytics: StudyAnalyticsItem[] = studies.map((study, index) => {
    const metrics = studyMetricsResults[index];
    const views = metrics?.views || 0;
    const completions = metrics?.completions || 0;
    const uniqueViewers = metrics?.uniqueViewers?.length || 0;
    const completionRate = views > 0 ? Math.round((completions / views) * 100) : 0;

    return {
      studyId: study.id,
      title: study.title,
      authorName: study.authorName,
      views,
      completions,
      uniqueViewers,
      completionRate,
      createdAt: study.createdAt
    };
  });

  // Build course analytics items
  const courseAnalytics: CourseAnalyticsItem[] = courses.map((course, index) => {
    const metrics = courseMetricsResults[index];
    const views = metrics?.views || 0;
    const completions = metrics?.completions || 0;
    const uniqueViewers = metrics?.uniqueViewers?.length || 0;
    const completionRate = views > 0 ? Math.round((completions / views) * 100) : 0;

    return {
      courseId: course.id,
      title: course.title,
      authorName: course.authorName,
      views,
      completions,
      uniqueViewers,
      completionRate,
      studyCount: course.studyIds?.length || 0,
      createdAt: course.createdAt
    };
  });

  // Calculate question analytics
  const questionAnalytics = calculateQuestionAnalytics(allQuestions);

  // Calculate learner stats
  const learnerStats = calculateLearnerStats(members);

  // Calculate overview metrics
  const totalViews = studyAnalytics.reduce((sum, s) => sum + s.views, 0);
  const totalCompletions = studyAnalytics.reduce((sum, s) => sum + s.completions, 0);

  // Get unique learners across all studies
  const allUniqueViewers = new Set<string>();
  studyMetricsResults.forEach(metrics => {
    if (metrics?.uniqueViewers) {
      metrics.uniqueViewers.forEach(uid => allUniqueViewers.add(uid));
    }
  });

  const averageCompletionRate = studyAnalytics.length > 0
    ? Math.round(studyAnalytics.reduce((sum, s) => sum + s.completionRate, 0) / studyAnalytics.length)
    : 0;

  return {
    overview: {
      totalStudies: studies.length,
      totalCourses: courses.length,
      totalViews,
      totalCompletions,
      uniqueLearners: allUniqueViewers.size,
      averageCompletionRate
    },
    studies: studyAnalytics,
    courses: courseAnalytics,
    questions: questionAnalytics,
    learners: learnerStats
  };
};

/**
 * Calculate question engagement metrics
 */
const calculateQuestionAnalytics = (questions: Question[]): QuestionAnalytics => {
  const totalQuestions = questions.length;
  const unreadQuestions = questions.filter(q => !q.isRead).length;
  const answeredQuestions = questions.filter(q => q.replies && q.replies.length > 0).length;
  const responseRate = totalQuestions > 0
    ? Math.round((answeredQuestions / totalQuestions) * 100)
    : 0;

  return {
    totalQuestions,
    unreadQuestions,
    answeredQuestions,
    responseRate
  };
};

/**
 * Calculate learner statistics from member profiles
 */
const calculateLearnerStats = (members: UserProfile[]): LearnerStats => {
  const now = Date.now();
  const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);

  // Count active learners (studied in last 7 days)
  const activeThisWeek = members.filter(m =>
    m.lastStudyDate && m.lastStudyDate > oneWeekAgo
  ).length;

  // Sort members by XP to get top learners
  const sortedByXP = [...members]
    .filter(m => (m.xp || 0) > 0)
    .sort((a, b) => (b.xp || 0) - (a.xp || 0))
    .slice(0, 10);

  const topLearners = sortedByXP.map(m => ({
    displayName: m.displayName || 'Anonymous',
    xp: m.xp || 0,
    completedCount: m.completedStudyIds?.length || 0
  }));

  return {
    totalMembers: members.length,
    activeThisWeek,
    topLearners
  };
};

/**
 * Get top performing studies sorted by a specific metric
 */
export const getTopStudies = (
  analytics: OrganizationAnalytics,
  sortBy: 'views' | 'completions' | 'rate' = 'completions',
  limit: number = 5
): StudyAnalyticsItem[] => {
  const sorted = [...analytics.studies].sort((a, b) => {
    switch (sortBy) {
      case 'views':
        return b.views - a.views;
      case 'completions':
        return b.completions - a.completions;
      case 'rate':
        return b.completionRate - a.completionRate;
      default:
        return b.completions - a.completions;
    }
  });

  return sorted.slice(0, limit);
};

/**
 * Sort studies by a specific metric
 */
export const sortStudies = (
  studies: StudyAnalyticsItem[],
  sortBy: 'views' | 'completions' | 'rate' | 'recent'
): StudyAnalyticsItem[] => {
  return [...studies].sort((a, b) => {
    switch (sortBy) {
      case 'views':
        return b.views - a.views;
      case 'completions':
        return b.completions - a.completions;
      case 'rate':
        return b.completionRate - a.completionRate;
      case 'recent':
        return b.createdAt - a.createdAt;
      default:
        return b.completions - a.completions;
    }
  });
};

/**
 * Sort courses by a specific metric
 */
export const sortCourses = (
  courses: CourseAnalyticsItem[],
  sortBy: 'views' | 'completions' | 'rate' | 'recent'
): CourseAnalyticsItem[] => {
  return [...courses].sort((a, b) => {
    switch (sortBy) {
      case 'views':
        return b.views - a.views;
      case 'completions':
        return b.completions - a.completions;
      case 'rate':
        return b.completionRate - a.completionRate;
      case 'recent':
        return b.createdAt - a.createdAt;
      default:
        return b.completions - a.completions;
    }
  });
};
